/* Pfanner — re-point a product card at the Gebinde the filter actually matched.
 *
 * WHY THIS EXISTS
 * ---------------
 * A product card renders exactly ONE variant (card-product.liquid picks
 * `selected_or_first_available_variant`, after a market fall-forward). The collection
 * filters are client-side and only toggle `hidden` on whole cards. So filtering by
 * anything that belongs to a Gebinde rather than to a product left the card pointing
 * somewhere else: selecting "Für unterwegs" surfaced Orangensaft because it sells a
 * 0,2 L, while the card kept showing, pricing and linking the 1 L. The Gebinde size
 * filter had carried the identical defect since it was written — pick 0,2 L, still get
 * the 1 L card.
 *
 * NOTHING HERE FORMATS ANYTHING. Every variant-dependent string — price, Grundpreis,
 * image src/srcset, variant URL — is pre-rendered by Liquid into `data-variants` and
 * this file only assigns it. `money` is locale-aware (de-AT), `unit_price_with_measurement`
 * has no JS equivalent, and image_url builds a CDN transform; reimplementing any of that
 * client-side is how a German price ends up rendering as "2.49" on /en.
 *
 * Only products that sell MORE THAN ONE single unit carry `data-variants` at all, so
 * this is inert on the large majority of cards. Trays are never candidates: an Anlass
 * belongs to a single unit and a tray never carries or inherits one.
 */
(function () {
  'use strict';

  var CACHE = new WeakMap();

  function read(card) {
    if (CACHE.has(card)) return CACHE.get(card);
    var raw = card.getAttribute('data-variants');
    var list = null;
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) list = parsed;
      } catch (e) {
        list = null; // malformed payload must never take the grid down
      }
    }
    CACHE.set(card, list);
    return list;
  }

  // Candidates satisfying EVERY active constraint at once. This is the fix for a second,
  // quieter bug: data-gebinde and data-anlass on the card are unions across its variants
  // and the filters are AND-combined, so "0,2 L" + "Familienpackung" used to match a
  // product where one variant satisfied each and NO single satisfied both.
  function candidates(list, gebinde, anlass) {
    return list.filter(function (v) {
      // v.x marks a variant that is in the payload only so the card can be restored to
      // what Liquid rendered — today that is usually a TRAY, because the card's default
      // variant frequently IS a tray (singles are inventory-tracked and read
      // unavailable, so first_available_variant falls through). A tray must never be
      // selectable by a filter: an Anlass belongs to a single unit.
      if (v.x) return false;
      if (gebinde && gebinde !== 'all' && v.g !== gebinde) return false;
      if (anlass && anlass !== 'all' && (v.a || '').indexOf('|' + anlass + '|') === -1) return false;
      return true;
    });
  }

  // Tie-break when several singles qualify. `prefer` comes from the chip
  // (data-anlass-prefer), not from a constant in here: "smallest" is right for
  // Für unterwegs and exactly backwards for Familienpackung, which wants the 2 L.
  function pick(matches, prefer) {
    if (matches.length < 2) return matches[0];
    return matches.reduce(function (best, v) {
      var a = typeof v.s === 'number' ? v.s : 0;
      var b = typeof best.s === 'number' ? best.s : 0;
      if (prefer === 'large') return a > b ? v : best;
      return a < b ? v : best;         // default: smallest, ties keep catalogue order
    });
  }

  function apply(card, v) {
    if (!v) return;

    card.querySelectorAll('[data-card-link]').forEach(function (a) {
      if (v.url) a.setAttribute('href', v.url);
    });

    var img = card.querySelector('[data-card-img]');
    if (img && v.src) {
      img.setAttribute('src', v.src);
      if (v.srcset) img.setAttribute('srcset', v.srcset);
    }

    var price = card.querySelector('[data-card-price]');
    if (price && v.price) price.textContent = v.price;

    // The strike only exists in the markup when the DEFAULT variant was on sale, so it
    // has to be created or removed rather than merely updated.
    var priceBox = price && price.parentNode;
    var cmp = card.querySelector('[data-card-compare]');
    if (priceBox) {
      if (v.cmp) {
        if (!cmp) {
          cmp = document.createElement('s');
          cmp.setAttribute('data-card-compare', '');
          priceBox.appendChild(cmp);
        }
        cmp.textContent = v.cmp;
      } else if (cmp) {
        cmp.remove();
      }
    }

    // .prod__perl is ALWAYS in the DOM even when empty — it reserves the line so prices
    // stay on one baseline across a row (see card-product.liquid). Keep that contract:
    // blank the text, never remove the element.
    var perl = card.querySelector('[data-card-perl]');
    if (perl) {
      perl.textContent = v.perl || '';
      if (v.perl) perl.removeAttribute('aria-hidden');
      else perl.setAttribute('aria-hidden', 'true');
    }

    var vid = card.querySelector('[data-card-vid]');
    if (vid) {
      vid.value = v.id;
      // The (+) is absent entirely on request-only / region-blocked / sold-out cards, so
      // if a button IS here the only thing that can disable it is this variant's stock.
      var btn = card.querySelector('.prod__add');
      var unavailable = v.avail === false;
      vid.disabled = unavailable;
      if (btn) {
        btn.disabled = unavailable;
        if (unavailable) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      }
    }

    // The price sort reads data-price off the card, so it must follow the re-point or
    // sorting a filtered grid orders it by prices no longer on screen.
    if (typeof v.p === 'number') card.setAttribute('data-price', v.p);
    card.setAttribute('data-v-current', v.id);
  }

  window.PfannerCardVariants = {
    /* Does any single of this card satisfy every active constraint?
       Cards without a payload fall back to the card-level union attributes, which is
       what every single-single product uses. */
    matches: function (card, gebinde, anlass) {
      var list = read(card);
      if (!list) {
        if (gebinde && gebinde !== 'all' && (card.getAttribute('data-gebinde') || '').indexOf('|' + gebinde + '|') === -1) return false;
        if (anlass && anlass !== 'all' && (card.getAttribute('data-anlass') || '').indexOf('|' + anlass + '|') === -1) return false;
        return true;
      }
      return candidates(list, gebinde, anlass).length > 0;
    },

    /* Point the card at the best matching single, or back at its default when no
       gebinde-scoped filter is active. Safe to call on every card, every apply(). */
    repoint: function (card, gebinde, anlass, prefer) {
      var list = read(card);
      if (!list) return;
      var target;
      if ((!gebinde || gebinde === 'all') && (!anlass || anlass === 'all')) {
        // Restore EXACTLY what Liquid rendered. No `|| list[0]` fallback: if the default
        // is somehow absent from the payload, doing nothing leaves the server's markup
        // intact, whereas guessing would silently change an unfiltered card.
        var def = card.getAttribute('data-v-default');
        target = list.filter(function (v) { return String(v.id) === String(def); })[0];
        if (!target) return;
      } else {
        target = pick(candidates(list, gebinde, anlass), prefer);
      }
      if (!target) return;
      if (card.getAttribute('data-v-current') === String(target.id)) return; // no-op
      apply(card, target);
    }
  };
})();
